import { ArrowPathIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import {
  CalendarIcon,
  PlayCircleIcon,
  ShareIcon,
} from '@heroicons/react/24/solid';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Loader } from '@/components/Loader';
import { OverflowText } from '@/components/OverflowText';
import { Container } from '@/components/ui';
import { useBulkCapabilityJob } from '@/hooks/useCapability';
import {
  CapabilitySchedule,
  useCreateCapabilitySchedule,
  useDeleteCapabilitySchedule,
  useListCapabilitySchedules,
  usePauseCapabilitySchedule,
  useResumeCapabilitySchedule,
  useUpdateCapabilitySchedule,
} from '@/hooks/useCapabilitySchedule';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { getSurfaceDisplayLabel } from '@/utils/capability.util';
import { logger } from '@/utils/logger';
import { startCase } from '@/utils/text.util';

import { CapabilityJobStatus } from './components/CapabilityJobStatus';
import { DrawerStep } from './components/DrawerStep';
import {
  ExecutionModeSelector,
  type ScheduleConfig,
} from './components/ExecutionModeSelector';
import { ExistingSchedulesPanel } from './components/ExistingSchedulesPanel';
import { InternalCapabilityCallout } from './components/InternalCapabilityCallout';
import { ParametersSection } from './components/sections';
import { AgentSelector } from './components/selectors/AgentSelector';
import { TargetSelector } from './components/selectors/TargetSelector';
import { ValidationSummary } from './components/ValidationSummary';
import { type WeeklyScheduleState } from './components/WeeklySchedule';
import { useCapabilityDrawerData } from './hooks/useCapabilityDrawerData';
import { useDrawerValidation } from './hooks/useDrawerValidation';
import {
  DeleteScheduleModal,
  EditScheduleModal,
  PauseScheduleModal,
} from './scheduling/modals';
import { createDefaultWeeklySchedule } from './utils/schedule-utils';

/**
 * CapabilityDrawer
 *
 * KEY-BASED PATTERN: Uses capabilityDrawerKey from URL params as the single
 * source of truth. The drawer fetches its own data internally via
 * useCapabilityDrawerData(key), eliminating race conditions from dual state sources.
 *
 * STEP-BASED UI: Linear 3-step flow replacing tab navigation:
 * - Step 1: CONFIGURE - Parameters and agent selection
 * - Step 2: SELECT TARGETS - Target asset selection
 * - Step 3: EXECUTION - Run Now or Schedule for Later
 */
export function CapabilityDrawer() {
  // KEY-BASED PATTERN: Read key from URL params via drawerState
  const { drawerState } = useGlobalState();
  const capabilityKey = drawerState.capabilityDrawerKey || '';

  // Fetch data internally using the key (single source of truth)
  const { capability, isLoading } = useCapabilityDrawerData(capabilityKey);

  const isPraetorianUser = useAuth().isPraetorianUser;
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [parameterValues, setParameterValues] = useState<
    Record<string, string>
  >({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({});
  const contentRef = useRef<HTMLDivElement>(null);

  // Add local state for big output checkbox
  const [largeArtifact, setLargeArtifact] = useState<boolean>(false);

  // Execution mode: 'now' or 'schedule'
  const [executionMode, setExecutionMode] = useState<'now' | 'schedule'>('now');

  // Schedule configuration state
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
    startDate: '',
    endDate: '',
    schedule: createDefaultWeeklySchedule(),
  });

  // Existing schedules panel state
  const [showSchedulesPanel, setShowSchedulesPanel] = useState(false);

  // Schedule modal states
  const [editingSchedule, setEditingSchedule] =
    useState<CapabilitySchedule | null>(null);
  const [deletingSchedule, setDeletingSchedule] =
    useState<CapabilitySchedule | null>(null);
  const [pausingSchedule, setPausingSchedule] =
    useState<CapabilitySchedule | null>(null);

  // Hooks for schedule management
  const { data: allSchedules = [] } = useListCapabilitySchedules();
  const { mutateAsync: createSchedule, isPending: isCreatingSchedule } =
    useCreateCapabilitySchedule();
  const { mutateAsync: deleteSchedule } = useDeleteCapabilitySchedule();
  const { mutateAsync: updateSchedule } = useUpdateCapabilitySchedule();
  const { mutateAsync: pauseSchedule } = usePauseCapabilitySchedule();
  const { mutateAsync: resumeSchedule } = useResumeCapabilitySchedule();
  const { mutateAsync: runJobs, isPending: isRunningJobs } =
    useBulkCapabilityJob();

  // Filter schedules for current capability
  const capabilitySchedules = useMemo(
    () => allSchedules.filter(s => s.capabilityName === capability?.name),
    [allSchedules, capability?.name]
  );

  // KEY-BASED PATTERN: State reset is handled by key={capabilityDrawerKey} in drawers/index.tsx
  // This forces remount when capability changes, automatically resetting all useState hooks.
  // No useEffect needed for state reset - React's key prop pattern handles this cleanly.

  // Derive default parameters from capability (computed, not stored in state)
  const defaultParams = useMemo(() => {
    if (!capability?.parameters) return {};
    return Object.fromEntries(
      capability.parameters
        .filter(p => p.default)
        .map(p => [p.name, p.default as string])
    );
  }, [capability]);

  // Merge default params with user edits (user edits take precedence)
  const mergedParameterValues = useMemo(
    () => ({ ...defaultParams, ...parameterValues }),
    [defaultParams, parameterValues]
  );

  // Use validation hook for centralized validation state
  const { isConfigValid, hasTargets, canExecute, validationSummary } =
    useDrawerValidation({
      capability,
      selectedAgent,
      selectedTargets,
      parameterValues: mergedParameterValues,
      validationErrors,
    });

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <Container className="flex size-full items-center justify-center">
        <Loader isLoading={true} />
      </Container>
    );
  }

  if (!capability) {
    return null;
  }

  const isInternalSurface = capability.surface === 'internal';
  const hasInput = capability.target !== 'noinput';

  // Helper functions for target display labels
  const getTargetLabel = () => {
    if (!capability?.target) return 'targets';
    const target = capability.target.toLowerCase();
    if (target === 'addomain') return 'AD Domains';
    if (target === 'repository') return 'Repositories';
    return `${startCase(target)}s`;
  };

  const getSingularTargetLabel = () => {
    if (!capability?.target) return 'target';
    const target = capability.target.toLowerCase();
    if (target === 'addomain') return 'AD Domain';
    return startCase(target);
  };

  // Validate all required parameters are filled
  const validateParameters = () => {
    const errors: Record<string, boolean> = {};
    let hasErrors = false;

    capability.parameters?.forEach(param => {
      const value = mergedParameterValues[param.name]?.trim();
      // Check if parameter is optional based on description or has default value
      const isRequired = param.required;
      if (isRequired && !value) {
        errors[param.name] = true;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Validate a single parameter on blur (real-time validation)
  const handleParameterBlur = (paramName: string) => {
    const param = capability.parameters?.find(p => p.name === paramName);
    if (!param || !param.required) return;

    const value = mergedParameterValues[paramName]?.trim();
    if (!value) {
      setValidationErrors(prev => ({
        ...prev,
        [paramName]: true,
      }));
    }
  };

  // Handler for running capability immediately
  const handleRunCapability = async () => {
    // Validate parameters before execution
    if (!validateParameters()) {
      toast.error('Please fill in all required parameters');
      return;
    }

    try {
      await runJobs(
        (hasInput ? selectedTargets : ['']).map(target => {
          // Build config object - add aegis parameter and client_id for internal surface capabilities
          const config = { ...mergedParameterValues };

          if (isInternalSurface && selectedAgent) {
            config.aegis = 'true';
            config.client_id = selectedAgent;
          }
          config.largeArtifact = String(largeArtifact);
          return {
            assetKey: target,
            capabilities: [capability.name],
            config,
          };
        })
      );

      // Clear selected targets and agent (for internal surface) after successful execution
      if (isInternalSurface) {
        setSelectedAgent('');
      }
      setSelectedTargets([]);
    } catch (error) {
      // Error handling is done in the useRunCapability hook
      console.error('Error running capability:', error);
    }
  };

  // Handler for saving scheduled execution (creating new schedule)
  const handleScheduleSave = async () => {
    // Validate required fields
    if (!scheduleConfig.startDate) {
      toast.error('Please select a start date');
      return;
    }

    if (isInternalSurface && !selectedAgent) {
      toast.error('Please select an agent');
      return;
    }

    if (hasInput && selectedTargets.length === 0) {
      toast.error('Please select at least one target');
      return;
    }

    try {
      // Convert dates to RFC3339 format
      const startDate = new Date(scheduleConfig.startDate).toISOString();
      const endDate = scheduleConfig.endDate
        ? new Date(scheduleConfig.endDate).toISOString()
        : undefined;

      // Create a schedule for each selected target (matching the pattern used in execution)
      const targets = hasInput ? selectedTargets : [''];
      const schedulePromises = targets.map(targetKey => {
        // Build config object - add aegis and client_id for internal surface capabilities
        const config = { ...mergedParameterValues };

        if (isInternalSurface && selectedAgent) {
          config.aegis = 'true';
          config.client_id = selectedAgent;
        }

        return createSchedule({
          capabilityName: capability.name,
          clientId: isInternalSurface ? selectedAgent : undefined, // Only include for internal capabilities
          targetKey, // Create one schedule per target
          config, // Credentials provided via parameters (Username, Password, etc.) + aegis/client_id for internal
          weeklySchedule: scheduleConfig.schedule,
          startDate,
          endDate,
        });
      });

      const results = await Promise.allSettled(schedulePromises);
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        toast.error(
          `${failures.length} of ${targets.length} schedules failed to create`
        );
      }

      // Reset to Run Now mode after successful creation
      setExecutionMode('now');
      setScheduleConfig({
        startDate: '',
        endDate: '',
        schedule: createDefaultWeeklySchedule(),
      });
    } catch (error) {
      // Error handling is done by the hook
      console.error('Failed to create schedule:', error);
    }
  };

  // ============================================================================
  // Schedule Panel Handlers
  // ============================================================================

  // Handler for confirming schedule deletion
  const handleConfirmDelete = async (scheduleId: string) => {
    try {
      await deleteSchedule(scheduleId);
      toast.success('Schedule deleted successfully');
      setDeletingSchedule(null);
    } catch (error) {
      logger.error('Failed to delete schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  // Handler for confirming schedule pause
  const handleConfirmPause = async (scheduleId: string) => {
    try {
      await pauseSchedule(scheduleId);
      toast.success('Schedule paused successfully');
      setPausingSchedule(null);
    } catch (error) {
      logger.error('Failed to pause schedule:', error);
      toast.error('Failed to pause schedule');
    }
  };

  // Handler for resuming a paused schedule
  const handleResumeSchedule = async (schedule: CapabilitySchedule) => {
    if (schedule.status === 'paused') {
      try {
        await resumeSchedule(schedule.scheduleId);
        toast.success('Schedule resumed successfully');
      } catch (error) {
        logger.error('Failed to resume schedule:', error);
        toast.error('Failed to resume schedule');
      }
    }
  };

  // Handler for saving schedule edits
  const handleSaveEdit = async (
    scheduleId: string,
    updates: {
      weeklySchedule: WeeklyScheduleState;
      startDate: string;
      endDate?: string;
    }
  ) => {
    const startDateISO = new Date(updates.startDate).toISOString();
    const endDateISO = updates.endDate
      ? new Date(updates.endDate).toISOString()
      : undefined;

    await updateSchedule({
      scheduleId,
      updates: {
        weeklySchedule: updates.weeklySchedule,
        startDate: startDateISO,
        endDate: endDateISO,
      },
    });
    setEditingSchedule(null);
  };

  // Handler for "Create New Schedule" from the panel (switches to schedule mode)
  const handleCreateNewFromPanel = () => {
    setShowSchedulesPanel(false);
    setExecutionMode('schedule');
  };

  // Get dynamic button label based on mode and target count
  const getButtonLabel = () => {
    const targetCount = selectedTargets.length;
    const targetName =
      targetCount === 1 ? getSingularTargetLabel() : getTargetLabel();

    if (!hasInput) {
      return executionMode === 'now' ? 'Run' : 'Create Schedule';
    }

    if (executionMode === 'now') {
      return `Run on ${targetCount} ${targetName}`;
    }

    return `Create Schedule for ${targetCount}`;
  };

  return (
    <Container className="relative size-full overflow-hidden">
      <div className="flex size-full flex-col overflow-hidden">
        {/* Header */}
        <div className="border-chariot-stroke flex w-full flex-col gap-4 border-b p-4 sm:p-6">
          {/* Row 1: Full-width capability name with share button */}
          <div className="flex items-center gap-2">
            <CopyToClipboard
              alwaysShowCopyIcon
              className="min-w-0 flex-1 gap-2 overflow-hidden [&_svg]:ml-0"
            >
              <OverflowText
                className="text-xl font-bold text-nowrap"
                text={capability.title || capability.name}
              />
            </CopyToClipboard>
            <Button
              styleType="icon"
              className="shrink-0 p-0"
              startIcon={
                <ShareIcon className="text-chariot-text-primary size-4" />
              }
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success('Copied URL to clipboard');
              }}
              data-testid="share-capability"
              label={''}
            />
          </div>

          {/* Row 2: Surface label (left) | Job status (right) */}
          <div className="flex items-start justify-between gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden">
              <p className="text-chariot-text-tertiary text-sm">
                {getSurfaceDisplayLabel(capability.surface)} Capability
              </p>
            </div>
            <div className="flex shrink-0 items-start gap-2">
              <CapabilityJobStatus capability={capability} />
            </div>
          </div>
        </div>

        {/* Content - Scrollable area for steps */}
        <div
          ref={contentRef}
          className="flex-1 space-y-4 overflow-auto p-4 sm:space-y-6 sm:p-6"
        >
          {/* Description */}
          <p className="text-chariot-text-secondary text-sm">
            {capability.description}
          </p>

          {/* Step 1: CONFIGURE */}
          <DrawerStep number={1} title="Configure" isValid={isConfigValid}>
            {/* Internal Capability Callout - explains why agent is required */}
            {isInternalSurface && isPraetorianUser && (
              <div className="mb-4">
                <InternalCapabilityCallout
                  onLearnMore={() => {
                    // Open Aegis documentation in new tab
                    window.open(
                      'https://docs.praetorian.com/aegis',
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                />
              </div>
            )}

            {/* Agent Selector for internal capabilities */}
            {isInternalSurface && isPraetorianUser && (
              <div className="mb-4">
                <AgentSelector
                  selectedAgent={selectedAgent}
                  onAgentChange={setSelectedAgent}
                />
              </div>
            )}

            {/* Parameters Section */}
            <ParametersSection
              capability={capability}
              parameterValues={mergedParameterValues}
              setParameterValues={setParameterValues}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              isPraetorianUser={isPraetorianUser}
              largeArtifact={largeArtifact}
              setLargeArtifact={setLargeArtifact}
              onParameterBlur={handleParameterBlur}
            />
          </DrawerStep>

          {/* Step 2: SELECT TARGETS - Only show if capability requires input */}
          {hasInput && isPraetorianUser && (
            <DrawerStep number={2} title="Select Targets" isValid={hasTargets}>
              <div className="text-chariot-text-secondary mb-2 text-sm">
                This capability scans:{' '}
                <span className="font-medium">{getTargetLabel()}</span>
              </div>
              <TargetSelector
                selectedTargets={selectedTargets}
                onTargetsChange={setSelectedTargets}
                capability={capability}
                getTargetLabel={getTargetLabel}
              />
              <div className="text-chariot-text-tertiary mt-2 text-sm">
                {selectedTargets.length}{' '}
                {selectedTargets.length === 1
                  ? getSingularTargetLabel().toLowerCase()
                  : getTargetLabel().toLowerCase()}{' '}
                selected
              </div>
            </DrawerStep>
          )}

          {/* Step 3: EXECUTION - Only for Praetorian users */}
          {isPraetorianUser && (
            <DrawerStep
              number={hasInput ? 3 : 2}
              title="Execution"
              isValid={canExecute}
            >
              <ExecutionModeSelector
                mode={executionMode}
                onModeChange={setExecutionMode}
                scheduleConfig={scheduleConfig}
                onScheduleConfigChange={setScheduleConfig}
                showLargeArtifact={!!capability.largeArtifact}
                largeArtifact={largeArtifact}
                onLargeArtifactChange={setLargeArtifact}
              />
              {executionMode === 'schedule' && selectedTargets.length > 1 && (
                <p className="text-chariot-text-tertiary mt-4 text-sm">
                  This will create {selectedTargets.length} schedules (one per
                  target)
                </p>
              )}
            </DrawerStep>
          )}
        </div>

        {/* Sticky Footer - Only for Praetorian users */}
        {isPraetorianUser && (
          <div className="border-chariot-stroke flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <ValidationSummary
                canExecute={canExecute}
                message={validationSummary}
              />
              {/* View Existing Schedules link - only show if schedules exist */}
              {capabilitySchedules.length > 0 && (
                <button
                  onClick={() => setShowSchedulesPanel(true)}
                  className="text-chariot-primary hover:text-chariot-primary-hover flex items-center gap-1.5 text-sm transition-colors focus:underline focus:outline-none"
                  data-testid="view-schedules-link"
                >
                  <CalendarDaysIcon className="size-4" />
                  <span>
                    View {capabilitySchedules.length} Schedule
                    {capabilitySchedules.length !== 1 ? 's' : ''}
                  </span>
                </button>
              )}
            </div>
            {executionMode === 'now' ? (
              <Button
                size="sm"
                styleType="primary"
                startIcon={
                  isRunningJobs ? (
                    <ArrowPathIcon className="size-4 animate-spin" />
                  ) : (
                    <PlayCircleIcon className="size-4" />
                  )
                }
                label={isRunningJobs ? 'Running...' : getButtonLabel()}
                disabled={!canExecute || isRunningJobs}
                onClick={handleRunCapability}
                data-testid="run-capability-button"
              />
            ) : (
              <Button
                size="sm"
                styleType="primary"
                startIcon={
                  isCreatingSchedule ? (
                    <ArrowPathIcon className="size-4 animate-spin" />
                  ) : (
                    <CalendarIcon className="size-4" />
                  )
                }
                label={isCreatingSchedule ? 'Creating...' : getButtonLabel()}
                disabled={!canExecute || isCreatingSchedule}
                onClick={handleScheduleSave}
                data-testid="create-schedule-button"
              />
            )}
          </div>
        )}

        {/* Existing Schedules Panel - Slides in from right */}
        <ExistingSchedulesPanel
          capabilityName={capability.title || capability.name}
          capabilityKey={capability.name}
          schedules={capabilitySchedules}
          isOpen={showSchedulesPanel}
          onClose={() => setShowSchedulesPanel(false)}
          onCreateNew={handleCreateNewFromPanel}
          onEdit={setEditingSchedule}
          onPause={setPausingSchedule}
          onResume={handleResumeSchedule}
          onDelete={setDeletingSchedule}
        />
      </div>

      {/* Schedule Modals */}
      <EditScheduleModal
        schedule={editingSchedule}
        isOpen={!!editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSave={handleSaveEdit}
      />

      <DeleteScheduleModal
        schedule={deletingSchedule}
        isOpen={!!deletingSchedule}
        onClose={() => setDeletingSchedule(null)}
        onConfirm={handleConfirmDelete}
      />

      <PauseScheduleModal
        schedule={pausingSchedule}
        isOpen={!!pausingSchedule}
        onClose={() => setPausingSchedule(null)}
        onConfirm={handleConfirmPause}
      />
    </Container>
  );
}
